"use client";

import { useTranslations } from "next-intl";
import messages from "@/i18n/messages/en.json";
import { useId, useMemo, useState } from "react";
import { Button, DropdownSelect, LocationInput, PhoneInput } from "@artha/ui";
import { createGooglePlacesProvider } from "../../../common/services/googlePlaces";
import { toReviewLocation, updateReviewLocation } from "../../personalization.utils";
import { normalizeCountryCode } from "@artha/utils";
import type { ReviewStepProps } from "../../personalization.types";
import {
  PERSONALIZATION_FIELD_CLASS_NAME,
  PERSONALIZATION_FIELD_LABEL_CLASS_NAME,
  RESUME_NO_WORK_MODE,
  RESUME_REVIEW_FIELDS,
  RESUME_WORK_MODES,
} from "../../personalization.constants";

export function ReviewStep({
  form,
  isRequestingCode,
  onChange,
  onSubmit,
  submitLabel,
  requestingLabel,
}: ReviewStepProps) {
  const t = useTranslations("profile.resumePersonalization");
  const id = useId();
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const locationProvider = useMemo(() => createGooglePlacesProvider(), []);
  const workModeOptions = [
    ...RESUME_WORK_MODES.map((value) => ({
      value,
      label: t(`review.workModeOptions.${value}`),
    })),
    { value: RESUME_NO_WORK_MODE, label: t("review.workModeOptions.noPreference") },
  ];

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (isResolvingLocation) return;
        onSubmit();
      }}
    >
      {RESUME_REVIEW_FIELDS.map(([field, isRequired]) => {
        const label = t(`review.${field}`);
        const placeholder = t(`review.${field}Placeholder`);
        const fieldId = `${id}-${field}`;
        if (field === "phone") {
          return (
            <PhoneInput
              key={field}
              id={fieldId}
              value={form.phone}
              defaultCountry={normalizeCountryCode(form.country)}
              required={false}
              label={label}
              placeholder={placeholder}
              countryLabel={t("review.phoneCountry")}
              searchPlaceholder={t("review.phoneCountrySearch")}
              emptyLabel={t("review.phoneCountryEmpty")}
              description={messages.common.phoneOtpHint}
              labelClassName={PERSONALIZATION_FIELD_LABEL_CLASS_NAME}
              controlClassName={PERSONALIZATION_FIELD_CLASS_NAME}
              onChange={(phone) => onChange({ ...form, phone })}
            />
          );
        }
        return (
          <div key={field} className="space-y-1.5">
            <label
              htmlFor={fieldId}
              className={PERSONALIZATION_FIELD_LABEL_CLASS_NAME}
            >
              {label}
              {isRequired ? (
                <span className="text-[#9D1C1C] ml-0.5">*</span>
              ) : null}
            </label>
            {field === "workMode" ? (
              <DropdownSelect
                id={fieldId}
                ariaLabel={label}
                value={form.workMode || RESUME_NO_WORK_MODE}
                options={workModeOptions}
                placeholder={placeholder}
                className={PERSONALIZATION_FIELD_CLASS_NAME}
                onValueChange={(value) =>
                  onChange({
                    ...form,
                    workMode: value === RESUME_NO_WORK_MODE ? "" : value,
                  })
                }
              />
            ) : (
              <input
                id={fieldId}
                type={
                  field === "email"
                    ? "email"
                    : field === "experienceYears"
                      ? "number"
                      : "text"
                }
                min={field === "experienceYears" ? 0 : undefined}
                max={field === "experienceYears" ? 80 : undefined}
                step={field === "experienceYears" ? 1 : undefined}
                aria-label={label}
                required={isRequired}
                value={form[field]}
                placeholder={placeholder}
                className={PERSONALIZATION_FIELD_CLASS_NAME}
                onChange={(event) =>
                  onChange({ ...form, [field]: event.target.value })
                }
              />
            )}
            {field === "titles" || field === "skills" ? (
              <span className="block text-xs text-[#667085]">
                {t("review.commaHint")}
              </span>
            ) : null}
          </div>
        );
      })}
      <LocationInput
        value={toReviewLocation(form)}
        onChange={(location) => onChange(updateReviewLocation(form, location))}
        provider={locationProvider}
        onResolvingChange={setIsResolvingLocation}
        labelClassName={PERSONALIZATION_FIELD_LABEL_CLASS_NAME}
        controlClassName={PERSONALIZATION_FIELD_CLASS_NAME}
      />
      <Button
        type="submit"
        className="h-12 w-full rounded-[10px] bg-primary font-bold text-primary-foreground"
        disabled={isRequestingCode || isResolvingLocation}
      >
        {isRequestingCode
          ? requestingLabel || t("actions.sendingCode")
          : submitLabel || t("review.submitCta")}
      </Button>
    </form>
  );
}
